import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { Dialog } from 'primeng/dialog';
import { PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import {
  SESSION_WARNING_THRESHOLD,
  SessionTimeoutService
} from '@core/services';

export interface SessionTimeoutStrings {
  header: string;
  message: string;
  logOut: string;
  continueSession: string;
  closeAriaLabel: string;
}

export const DEFAULT_SESSION_TIMEOUT_STRINGS: SessionTimeoutStrings = {
  header: 'Session Timeout Warning',
  message:
    'Your session is about to end. Choose Continue Session to stay signed in, or you will be signed out automatically.',
  logOut: 'Log Out',
  continueSession: 'Continue Session',
  closeAriaLabel:
    'Dismiss this warning. Your session will still end when the countdown reaches zero.'
};

/**
 * App-wide session expiry warning. Mounted once in `app-main` (the layout shell
 * shared by nymbl2, nymbl3, and the LMN therapist route), so every
 * authenticated screen gets the warning rather than just the one screen that
 * used to have it.
 *
 * Knows nothing about auth mechanisms or routes: the remaining time, the
 * extension call, and the post-logout destination all come from
 * SESSION_SERVICE, which the LMN route and nymbl3 override with their own
 * implementations. SessionTimeoutService is provided here, not in the root
 * injector, so those route-level overrides are the ones it resolves.
 */
@Component({
  selector: 'nymbl-session-timeout',
  templateUrl: './session-timeout.component.html',
  imports: [Dialog, PrimeTemplate, Button],
  providers: [SessionTimeoutService]
})
export class SessionTimeoutComponent implements OnInit {
  private sessionTimeout = inject(SessionTimeoutService);
  private destroyRef = inject(DestroyRef);

  protected visible = signal(false);
  protected countdownDisplay = signal(this.format(SESSION_WARNING_THRESHOLD));
  protected extending = signal(false);

  /**
   * Set when the user dismisses the warning by any means other than the
   * continue action. The countdown keeps running and the session still ends at
   * zero — a dismissal is not an extension — but the dialog does not reopen a
   * second later, which would make it impossible to dismiss at all.
   */
  private dismissed = false;

  /** Guards against a second endSession() from the zero tick after the first. */
  private ending = false;

  /**
   * Copy is an input with defaults, per the props-based i18n pattern in
   * `nymbl2/CLAUDE.md`: this component is mounted by the layout shell in two
   * apps with different translation mechanisms (nymbl2 has none wired up yet,
   * nymbl3 uses ngx-translate), so the mount site owns localization.
   */
  strings = input<SessionTimeoutStrings>(DEFAULT_SESSION_TIMEOUT_STRINGS);

  ngOnInit(): void {
    this.sessionTimeout.countdown$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((remaining) => this.onCountdown(remaining));

    this.sessionTimeout.startCountdown();
  }

  private onCountdown(remaining: number): void {
    if (remaining <= 0) {
      this.endSession();
      return;
    }

    if (remaining > SESSION_WARNING_THRESHOLD) {
      // Back above the threshold after an extension: the next crossing should
      // warn again, so forget any earlier dismissal.
      this.dismissed = false;
      this.visible.set(false);
      return;
    }

    this.countdownDisplay.set(this.format(remaining));
    this.visible.set(!this.dismissed);
  }

  /**
   * Ends the session and lets SESSION_SERVICE navigate. The dialog is hidden
   * first so no warning flashes on the way out.
   */
  protected endSession(): void {
    if (this.ending) {
      return;
    }
    this.ending = true;
    this.visible.set(false);
    this.sessionTimeout.endSession().subscribe();
  }

  /**
   * Extends the session. A failed extension logs the user out — closing the
   * warning and leaving them on a session that was never extended is the exact
   * bug this dialog exists to prevent.
   *
   * Visibility is not touched on success: the extension re-emits the new
   * remaining time, so the countdown handler hides the dialog, or keeps it open
   * when the new expiry is itself already inside the warning window.
   */
  protected continueSession(): void {
    this.extending.set(true);
    this.sessionTimeout
      .extendSession()
      .pipe(finalize(() => this.extending.set(false)))
      .subscribe({
        next: () => (this.dismissed = false),
        error: () => this.endSession()
      });
  }

  /**
   * Any close that is not the continue action — the close control, escape, or a
   * click outside. Hiding the dialog programmatically also lands here, which is
   * harmless: continueSession() clears the flag after the countdown handler has
   * already decided whether to stay open.
   */
  protected onHide(): void {
    this.dismissed = true;
  }

  /** `m:ss`, floored so the display reads 0:00 in the final second. */
  private format(remaining: number): string {
    const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }
}
